(function(app) {
    // Canvas Context Module with improved performance
    const CanvasContext = {
        canvas: null,
        ctx: null,
        ADOBE_DPI: 72, // Adobe's standard DPI
        // Add offscreen canvas for measurements
        offscreenCanvas: null,
        offscreenCtx: null,

        getContext() {
            if (!this.canvas) {
                // Use OffscreenCanvas for measurements when available
                if (typeof OffscreenCanvas !== 'undefined') {
                    // Create a high-resolution canvas for subpixel precision
                    this.offscreenCanvas = new OffscreenCanvas(1000, 1000);
                    this.offscreenCtx = this.offscreenCanvas.getContext('2d', {
                        alpha: true, // Enable alpha for more accurate glyph edges
                        willReadFrequently: true,
                        desynchronized: true // Improve performance
                    });

                    // Enable high-quality antialiasing for subpixel precision
                    if (this.offscreenCtx.imageSmoothingEnabled !== undefined) {
                        this.offscreenCtx.imageSmoothingEnabled = true;
                        this.offscreenCtx.imageSmoothingQuality = 'high';
                    }

                    return this.offscreenCtx;
                }

                // Fallback to regular canvas with high resolution
                this.canvas = document.createElement('canvas');
                this.canvas.width = 1000;
                this.canvas.height = 1000;
                const ctx = this.canvas.getContext('2d', {
                    alpha: true, // Enable alpha for more accurate glyph edges
                    willReadFrequently: true,
                    desynchronized: true // Improve performance
                });

                if (!ctx) {
                    throw new Error('Failed to create canvas context');
                }

                // Enable high-quality antialiasing for subpixel precision
                if (ctx.imageSmoothingEnabled !== undefined) {
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                }

                this.ctx = ctx;
            }
            return this.offscreenCtx || this.ctx;
        },

        cleanup() {
            this.canvas = null;
            this.ctx = null;
            this.offscreenCanvas = null;
            this.offscreenCtx = null;
            TextMeasurer.clearCache();
        }
    };

    // Text Measurement Module with exact pixel measurements
    const TextMeasurer = {
        measurementCache: new Map(),
        dimensionsCache: new Map(),

        // Measure any character or text with pixel-perfect accuracy
        measureTextPixelPerfect(text, fontFamily, fontStyle, fontSize = 100) {
            // Use a temporary canvas for precise measurement
            const canvas = document.createElement('canvas');
            canvas.width = fontSize * Math.max(10, text.length * 2);  // Plenty of room for any text
            canvas.height = fontSize * 3;
            const ctx = canvas.getContext('2d', {
                alpha: true,
                willReadFrequently: true
            });

            ctx.textBaseline = 'alphabetic';
            ctx.font = `${fontStyle} ${fontSize}px ${fontFamily}`;
            ctx.fillStyle = 'white';
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Position the text with enough margin
            const x = fontSize / 2;
            const y = fontSize * 1.5;  // Place baseline lower on canvas

            // Draw the text
            ctx.fillText(text, x, y);

            // Get exact pixel data
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // Find the actual boundaries of the rendered text by analyzing pixels
            let top = canvas.height;
            let bottom = 0;
            let left = canvas.width;
            let right = 0;

            // Scan pixel data to find actual bounds of the text
            for (let py = 0; py < canvas.height; py++) {
                for (let px = 0; px < canvas.width; px++) {
                    const idx = (py * canvas.width + px) * 4;
                    // Check alpha channel (idx+3) for any visible pixel
                    if (data[idx+3] > 10) {  // Any non-fully-transparent pixel
                        top = Math.min(top, py);
                        bottom = Math.max(bottom, py);
                        left = Math.min(left, px);
                        right = Math.max(right, px);
                    }
                }
            }

            // If no visible pixels were found, return zero dimensions
            if (top === canvas.height || left === canvas.width) {
                return {
                    width: 0,
                    height: 0,
                    capHeight: 0,
                    descent: 0,
                    ascent: 0,
                    aspectRatio: 0
                };
            }

            // Calculate metrics
            const actualWidth = right - left + 1;
            const actualHeight = bottom - top + 1;
            const ascent = y - top;  // Distance from baseline to top
            const descent = bottom - y;  // Distance from baseline to bottom

            // Notify debug system if available
            if (typeof window.TextMeasurementDebug !== 'undefined') {
                window.TextMeasurementDebug.onPixelPerfectMeasurement(text, canvas, {
                    text: text,
                    x, y, left, right, top, bottom,
                    width: actualWidth,
                    height: actualHeight,
                    baselineFromTop: ascent,
                    ascent: ascent,
                    descent: descent
                });
            }

            return {
                width: actualWidth,
                height: actualHeight,
                capHeight: ascent,
                ascent: ascent,
                descent: descent,
                fontSize: fontSize,
                aspectRatio: actualWidth / actualHeight
            };
        },

        // Highly accurate H measurement function with visual verification
        measureCapitalH(fontFamily, fontStyle, fontSize = 100) {
            return this.measureTextPixelPerfect('H', fontFamily, fontStyle, fontSize);
        },

        // Debug function for test characters
        debugMeasureCharacter(char, fontFamily, fontStyle, fontSize = 100, desiredBaseHeightCM = 1) {
            // Get real H metrics for reference
            const hMetrics = this.measureCapitalH(fontFamily, fontStyle, fontSize);

            // Measure the test character
            const charMetrics = this.measureTextPixelPerfect(char, fontFamily, fontStyle, fontSize);

            // Calculate scaling to achieve desired base height
            const desiredBaseHeightPx = UnitConverter.centimetersToPixels(desiredBaseHeightCM);
            const scaleFactor = desiredBaseHeightPx / hMetrics.capHeight;

            // Scale character metrics
            const scaledWidth = charMetrics.width * scaleFactor;
            const scaledHeight = charMetrics.height * scaleFactor;

            // Metrics relative to H
            const heightRatioToH = charMetrics.height / hMetrics.height;
            const widthRatioToH = charMetrics.width / hMetrics.width;

            const dimensions = {
                char: char,
                widthPx: charMetrics.width,
                heightPx: charMetrics.height,
                widthCM: UnitConverter.pixelsToCentimeters(scaledWidth),
                heightCM: UnitConverter.pixelsToCentimeters(scaledHeight),
                ascentPx: charMetrics.ascent,
                descentPx: charMetrics.descent,
                aspectRatio: charMetrics.aspectRatio,
                heightRatioToH,
                widthRatioToH,
                scaleFactor
            };

            console.table([dimensions]);
            return dimensions;
        },

        measureTextDimensions(text, fontFamily, fontStyle, fontSize = 100, textAlign = 'left', leadingMultiplier = 1.2) {
            if (!text || typeof text !== 'string' || text.trim() === '') {
                return {
                    widthPx: 0,
                    heightPx: 0,
                    referenceCapHeightPx: 0,
                    firstLineAscentPx: 0,
                    lastLineDescentPx: 0,
                    minLeftBearingPx: 0,
                    maxRightBearingPx: 0,
                    maxLineWidthPx: 0,
                    numberOfLines: 0,
                    actualGlyphHeightForSingleLine: 0
                };
            }

            // Cache key based on all inputs to optimize performance
            const cacheKey = `${text}|${fontFamily}|${fontStyle}|${fontSize}|${textAlign}|${leadingMultiplier}`;
            if (this.dimensionsCache.has(cacheKey)) {
                return this.dimensionsCache.get(cacheKey);
            }

            // Debug measurements for test characters
            const currentBaseHeight = app.Store.getState('text-baseheight') || 1;
            if (text.length === 1 && 'HzjAXpq'.includes(text)) {
                this.debugMeasureCharacter(text, fontFamily, fontStyle, fontSize, currentBaseHeight);
            }

            // Get precise measurement of capital H for reference
            const hMeasurement = this.measureCapitalH(fontFamily, fontStyle, fontSize);
            const referenceCapHeightPx = hMeasurement.capHeight;

            // For single line text, we can use direct pixel-perfect measurement
            if (!text.includes('\n')) {
                // Direct pixel measurement for accuracy
                const directMeasurement = this.measureTextPixelPerfect(text, fontFamily, fontStyle, fontSize);

                return {
                    widthPx: directMeasurement.width,
                    heightPx: directMeasurement.height,
                    referenceCapHeightPx: referenceCapHeightPx,
                    firstLineAscentPx: directMeasurement.ascent,
                    lastLineDescentPx: directMeasurement.descent,
                    minLeftBearingPx: 0, // We don't need this for direct measurement
                    maxRightBearingPx: directMeasurement.width,
                    maxLineWidthPx: directMeasurement.width,
                    numberOfLines: 1,
                    actualGlyphHeightForSingleLine: directMeasurement.height,
                    lineMetrics: [{
                        text: text,
                        ascent: directMeasurement.ascent,
                        descent: directMeasurement.descent,
                        actualGlyphHeight: directMeasurement.height,
                        width: directMeasurement.width
                    }]
                };
            }

            // For multi-line text, use the original line-by-line approach
            const ctx = CanvasContext.getContext();
            ctx.save();

            // Configure context for high-precision rendering
            ctx.textBaseline = 'alphabetic'; // Most reliable baseline for measurements
            ctx.font = `${fontStyle} ${fontSize}px ${fontFamily}`;
            ctx.textAlign = textAlign;

            const lines = text.split('\n');
            const numberOfLines = lines.length;
            const referenceFontMetricsHeightPx = hMeasurement.height;

            let maxLineWidthEncountered = 0;
            let overallMinLeftBearing = 0;
            let overallMaxRightBearing = 0;

            const lineMetrics = [];

            for (const line of lines) {
                if (line.trim() === '') { // Handle empty lines by using reference font metrics
                    lineMetrics.push({
                        text: line,
                        ascent: referenceCapHeightPx,
                        descent: referenceFontMetricsHeightPx - referenceCapHeightPx,
                        actualGlyphHeight: referenceFontMetricsHeightPx,
                        width: 0, // Empty line has no width
                        leftBearing: 0,
                        rightBearing: 0
                    });
                    continue;
                }

                // Use pixel-perfect measurement for each line
                const lineMeasurement = this.measureTextPixelPerfect(line, fontFamily, fontStyle, fontSize);

                lineMetrics.push({
                    text: line,
                    ascent: lineMeasurement.ascent,
                    descent: lineMeasurement.descent,
                    actualGlyphHeight: lineMeasurement.height,
                    width: lineMeasurement.width,
                    leftBearing: 0,
                    rightBearing: lineMeasurement.width
                });

                maxLineWidthEncountered = Math.max(maxLineWidthEncountered, lineMeasurement.width);
                // We don't track bearings in pixel-perfect measurement the same way
                overallMaxRightBearing = Math.max(overallMaxRightBearing, lineMeasurement.width);
            }

            let totalHeightPx;
            let firstLineAscentForPositioning;
            const lastLineDescentPx = lineMetrics.length > 0 ? lineMetrics[lineMetrics.length - 1].descent : 0;
            let actualGlyphHeightForSingleLine = 0;

            if (numberOfLines === 1) {
                const singleLine = lineMetrics[0];
                totalHeightPx = singleLine.actualGlyphHeight;
                firstLineAscentForPositioning = singleLine.ascent;
                actualGlyphHeightForSingleLine = singleLine.actualGlyphHeight;
            } else {
                // Calculate baselineToBaselineSpacing with floating point precision
                const baselineToBaselineSpacing = fontSize * leadingMultiplier;

                // Calculate total height with subpixel precision
                let currentY = 0; // Baseline of the first line
                const firstLine = lineMetrics[0];
                let overallTopEdge = 0 - firstLine.ascent; // Highest point relative to first baseline

                // Iterate through lines to find the total span with subpixel precision
                for (let i = 0; i < numberOfLines - 1; i++) {
                    currentY += baselineToBaselineSpacing;
                }

                // Now currentY is the baseline of the last line
                const lastLine = lineMetrics[numberOfLines - 1];
                let overallBottomEdge = currentY + lastLine.descent; // Lowest point relative to first baseline

                totalHeightPx = overallBottomEdge - overallTopEdge;
                firstLineAscentForPositioning = 0 - overallTopEdge; // Distance from visual top to first baseline
            }

            // Calculate effective width with subpixel precision
            const effectiveWidthPx = overallMaxRightBearing - overallMinLeftBearing;

            // Return dimensions with full floating point precision
            const result = {
                widthPx: effectiveWidthPx,
                heightPx: totalHeightPx,
                referenceCapHeightPx: referenceCapHeightPx,
                firstLineAscentPx: firstLineAscentForPositioning,
                lastLineDescentPx: lastLineDescentPx,
                minLeftBearingPx: overallMinLeftBearing,
                maxRightBearingPx: overallMaxRightBearing,
                maxLineWidthPx: maxLineWidthEncountered,
                numberOfLines: numberOfLines,
                actualGlyphHeightForSingleLine: actualGlyphHeightForSingleLine,
                sumOfActualGlyphHeights: lineMetrics.reduce((sum, lm) => sum + lm.actualGlyphHeight, 0),
                lineMetrics: lineMetrics
            };

            ctx.restore();

            // Cache the precise results
            this.dimensionsCache.set(cacheKey, result);

            return result;
        },

        clearCache() {
            this.measurementCache.clear();
            this.dimensionsCache.clear();
        }
    };

    // Unit Conversion Module with exact precision
    const UnitConverter = {
        // Standard Adobe 72 DPI values
        ADOBE_DPI: 72,
        CM_PER_INCH: 2.54,

        // Get the pixel-to-cm ratio (pixels per cm)
        getPixelsPerCM() {
            return this.ADOBE_DPI / this.CM_PER_INCH;
        },

        pixelsToCentimeters(pixels) {
            // Use Adobe's 72 DPI standard with high precision
            const exactPixelsPerCm = this.getPixelsPerCM();
            // Return direct floating point value for maximum precision
            return pixels / exactPixelsPerCm;
        },

        centimetersToPixels(cm) {
            const exactPixelsPerCm = this.getPixelsPerCM();
            // Return direct floating point for subpixel precision
            return cm * exactPixelsPerCm;
        },

        // Format final output value for display with controlled precision
        formatOutputValue(value, decimals = 1) {
            // Only round at the final display step
            return parseFloat(value.toFixed(decimals));
        }
    };

    // Dimension Calculator Module with exact pixel-perfect calculations
    const DimensionCalculator = {
        calculateTextDimensionsCM(text, fontFamily, fontStyle, desiredBaseHeightCM, textAlign = 'left') {
            try {
                const flagContext = app.CanvasRenderer.getFlagContext();
                const precisionFontSize = 100; // Use a high but not excessive font size

                // Create cache key for performance optimization
                const cacheKey = `${text}|${fontFamily}|${fontStyle}|${desiredBaseHeightCM}|${textAlign}|${JSON.stringify(flagContext)}`;
                if (TextMeasurer.dimensionsCache.has(cacheKey)) {
                    return TextMeasurer.dimensionsCache.get(cacheKey);
                }

                // First measure capital H precisely to establish baseline
                const hMetrics = TextMeasurer.measureCapitalH(
                    fontFamily,
                    fontStyle,
                    precisionFontSize
                );

                // A baseHeight of 1.0 means H is exactly 1cm tall (its cap height)
                // Convert desired H height from CM to pixels
                const desiredHHeightPx = UnitConverter.centimetersToPixels(desiredBaseHeightCM);

                // Calculate scale factor: how much we need to scale measurements to achieve desired H height
                const scaleFactor = desiredHHeightPx / hMetrics.capHeight;

                // Calculate the expected H width at the target scale
                // This ensures the aspect ratio is preserved
                const targetHWidthPx = hMetrics.width * scaleFactor;

                // Calculate in CM what the H width should be
                const targetHWidthCM = UnitConverter.pixelsToCentimeters(targetHWidthPx);

                // Now measure the actual text directly with pixel-perfect accuracy
                const pxMetrics = TextMeasurer.measureTextDimensions(
                    text,
                    fontFamily,
                    fontStyle,
                    precisionFontSize,
                    textAlign
                );

                if (pxMetrics.referenceCapHeightPx === 0 && text.trim() !== '') {
                    console.warn('Reference Cap Height measured as 0. Check font or measurement logic.');
                }

                // If there's no text, or measurements are zero (e.g. only whitespace lines)
                if (pxMetrics.widthPx === 0 && pxMetrics.heightPx === 0 && text.trim() === '') {
                    return { widthCM: 0, heightCM: 0, baseHeightCM: desiredBaseHeightCM };
                }

                let finalWidthPx = pxMetrics.widthPx;
                let finalHeightPx = pxMetrics.heightPx;

                // Handle flag context if enabled with subpixel precision
                if (flagContext.isEnabled && flagContext.image) {
                    const img = flagContext.image;
                    const flagAspectRatio = img.width / img.height;

                    // IMPORTANT: Flag height is based on capital H, not full text height
                    // The flag height should be the cap height of H
                    const scaledFlagEffectiveHeightPx = pxMetrics.referenceCapHeightPx;
                    const flagWidthPxAtScale = scaledFlagEffectiveHeightPx * flagAspectRatio;

                    const scaledSpacingPx = flagContext.spacing * (precisionFontSize / flagContext.baseFontSizeForSpacing);

                    // Add flag width and scaled spacing to the text width (maintaining full precision)
                    finalWidthPx = pxMetrics.widthPx + flagWidthPxAtScale + scaledSpacingPx;
                    // Height remains the text height (with descenders/ascenders extending beyond flag)
                    finalHeightPx = pxMetrics.heightPx;
                }

                // Apply pixel scale factor directly
                const scaledWidthPx = finalWidthPx * scaleFactor;
                const scaledHeightPx = finalHeightPx * scaleFactor;

                // Calculate width in CM based on desired H aspect ratio
                // This is critical for ensuring width is proportionally correct
                const widthCM = UnitConverter.pixelsToCentimeters(scaledWidthPx);
                const heightCM = UnitConverter.pixelsToCentimeters(scaledHeightPx);

                const dimensions = {
                    widthCM: widthCM,
                    heightCM: heightCM,
                    baseHeightCM: desiredBaseHeightCM,
                    // Store pixel metrics for debugging
                    pixelMetrics: {
                        ...pxMetrics,
                        // Update width and height to reflect final scaled values
                        actualScaledWidthPx: scaledWidthPx,
                        actualScaledHeightPx: scaledHeightPx
                    },
                    // Additional debug information
                    _debug: {
                        scaleFactor,
                        hMetrics,
                        referenceCapHeightPx: pxMetrics.referenceCapHeightPx,
                        desiredBaseHeightPx: desiredHHeightPx,
                        unscaledWidthPx: finalWidthPx,
                        unscaledHeightPx: finalHeightPx,
                        scaledWidthPx: scaledWidthPx,
                        scaledHeightPx: scaledHeightPx,
                        // Add target H values for debug
                        targetHWidthPx: targetHWidthPx,
                        targetHHeightPx: hMetrics.height * scaleFactor,
                        targetHCapHeightPx: hMetrics.capHeight * scaleFactor,
                        // Add target H aspect ratio and width in CM
                        hAspectRatio: hMetrics.aspectRatio,
                        targetHWidthCM: targetHWidthCM,
                        pixelsPerCM: UnitConverter.getPixelsPerCM()
                    }
                };

                // Cache the result for future calls
                TextMeasurer.dimensionsCache.set(cacheKey, dimensions);

                // Notify debug system if available
                if (typeof window.TextMeasurementDebug !== 'undefined') {
                    window.TextMeasurementDebug.onMeasurementUpdate(dimensions);
                }

                return dimensions;

            } catch (err) {
                console.error('Error calculating text dimensions in CM:', err);
                return {widthCM: 0, heightCM: 0, baseHeightCM: 0};
            }
        },

        calculateFlagDimensions(flagContext, textMetrics, precisionFontSize) {
            const img = flagContext.image;
            const aspectRatio = img.width / img.height;

            const spacingAtPrecisionFontPx = flagContext.spacing *
                (precisionFontSize / flagContext.baseFontSizeForSpacing);

            const flagEffectiveHeightPx = textMetrics.height > 0 ?
                textMetrics.height : precisionFontSize;

            const flagWidthPx = flagEffectiveHeightPx * aspectRatio;
            const flagTotalContributionToWidthPx = flagWidthPx + spacingAtPrecisionFontPx;

            return {
                totalWidth: textMetrics.width + flagTotalContributionToWidthPx,
                totalHeight: Math.max(textMetrics.height, flagEffectiveHeightPx)
            };
        },

        calculateByBaseHeight(width = true, height = true) {
            const styles = app.CanvasRenderer.getStyles();
            const styleString = app.CanvasRenderer.getStyleString();
            let currentBaseHeight = parseFloat(app.Store.getState('text-baseheight')) || 1;

            // Get max width and max height from store
            const maxWidthCMString = app.Store.getState('text-maxwidth');
            const maxHeightCMString = app.Store.getState('text-maxheight');

            const maxWidthCM = parseFloat(maxWidthCMString);
            const maxHeightCM = parseFloat(maxHeightCMString);

            const hasMaxWidthConstraint = !isNaN(maxWidthCM) && maxWidthCM > 0;
            const hasMaxHeightConstraint = !isNaN(maxHeightCM) && maxHeightCM > 0;

            if (currentBaseHeight <= 0 && styles.text.trim() !== '') {
                console.warn('calculateByBaseHeight called with invalid baseHeight:', currentBaseHeight);
                if (styles.text.trim() === '') {
                    app.Store.setState('text-width', '0.00');
                    app.Store.setState('text-height', '0.00');
                }
                return;
            } else if (styles.text.trim() === '') {
                app.Store.setState('text-width', '0.00');
                app.Store.setState('text-height', '0.00');
                // Also set baseHeight to a sensible default or keep it, but ensure it's formatted.
                app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(parseFloat(app.Store.getState('text-baseheight')) || 1));
                return;
            }

            // IMPROVED: First measure the reference 'H' to establish correct metrics
            const precisionFontSize = 1000; // Large font size for precision
            const hMetrics = TextMeasurer.measureCapitalH(
                styles.font,
                styleString,
                precisionFontSize
            );

            // A baseHeight of 1.0 means H is exactly 1cm tall
            // Calculate the scale factor to achieve this
            const desiredHHeightInCM = currentBaseHeight;
            const desiredHHeightInPx = UnitConverter.centimetersToPixels(desiredHHeightInCM);
            const baseScaleFactor = desiredHHeightInPx / hMetrics.capHeight;

            // Now we can accurately calculate dimensions
            let dims = this.calculateTextDimensionsCM(
                styles.text,
                styles.font,
                styleString,
                currentBaseHeight,
                styles.justification
            );

            let scaleFactor = 1.0;
            let needsRescale = false;

            // Use precise floating point comparisons
            if (hasMaxWidthConstraint && dims.widthCM > maxWidthCM) {
                const widthScale = maxWidthCM / dims.widthCM;
                scaleFactor = Math.min(scaleFactor, widthScale);
                needsRescale = true;
            }

            if (hasMaxHeightConstraint && dims.heightCM > maxHeightCM) {
                const heightScale = maxHeightCM / dims.heightCM;
                scaleFactor = Math.min(scaleFactor, heightScale);
                needsRescale = true;
            }

            // If both constraints exist and were exceeded, scaleFactor will be min(widthScale, heightScale)
            if (needsRescale && scaleFactor < 1.0 && scaleFactor > 0) {
                currentBaseHeight *= scaleFactor;
                currentBaseHeight = Math.max(0.01, currentBaseHeight); // Ensure baseHeight is positive

                // Recalculate dimensions with the new baseHeight with full precision
                dims = this.calculateTextDimensionsCM(
                    styles.text,
                    styles.font,
                    styleString,
                    currentBaseHeight,
                    styles.justification
                );
            }

            // Always update baseHeight in store, with consistent formatting
            app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(currentBaseHeight));

            // Only apply final formatting when storing the values
            if (width) {
                app.Store.setState('text-width', UnitConverter.formatOutputValue(dims.widthCM));
            }
            if (height) {
                app.Store.setState('text-height', UnitConverter.formatOutputValue(dims.heightCM));
            }
        },

        calculateByWidth() {
            const styles = app.CanvasRenderer.getStyles();
            const styleString = app.CanvasRenderer.getStyleString();
            let desiredWidthCM = parseFloat(app.Store.getState('text-width')) || 0;
            let currentBaseHeight = parseFloat(app.Store.getState('text-baseheight')) || 1;
            if (currentBaseHeight <= 0) currentBaseHeight = 1;

            const maxWidthCMString = app.Store.getState('text-maxwidth');
            const maxHeightCMString = app.Store.getState('text-maxheight');
            const maxWidthCM = parseFloat(maxWidthCMString);
            const maxHeightCM = parseFloat(maxHeightCMString);
            const hasMaxWidthConstraint = !isNaN(maxWidthCM) && maxWidthCM > 0;
            const hasMaxHeightConstraint = !isNaN(maxHeightCM) && maxHeightCM > 0;

            // If a max width is set and the desired width exceeds it, cap the desired width.
            if (hasMaxWidthConstraint && desiredWidthCM > maxWidthCM) {
                desiredWidthCM = maxWidthCM;
                // Update the store with properly formatted value
                app.Store.setState('text-width', UnitConverter.formatOutputValue(desiredWidthCM));
            }

            if (styles.text.trim() === '' || desiredWidthCM <= 0) {
                app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(currentBaseHeight));
                this.calculateByBaseHeight(true, true); // This will set width/height to 0 if no text
                if (styles.text.trim() === '') app.Store.setState('text-width', '0.00');
                else if (desiredWidthCM <= 0) app.Store.setState('text-width', '0.00');
                return;
            }

            try {
                // Calculate dimensions with a reference base height to find the scaling factor
                const referenceDims = this.calculateTextDimensionsCM(
                    styles.text,
                    styles.font,
                    styleString,
                    currentBaseHeight, // Use currentBaseHeight as the reference for scaling
                    styles.justification
                );

                if (referenceDims.widthCM <= 0) {
                    console.warn('Calculated reference width is zero or negative. Cannot scale by width.');
                    app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(currentBaseHeight));
                    // Update height based on current baseHeight, width might remain 0 or as is
                    this.calculateByBaseHeight(false, true);
                    return;
                }

                // Calculate precise scale factor without rounding
                const scaleFactorForWidth = desiredWidthCM / referenceDims.widthCM;
                let newBaseHeight = currentBaseHeight * scaleFactorForWidth;
                newBaseHeight = Math.max(0.01, newBaseHeight); // Ensure positive base height

                // Now, check if this newBaseHeight causes a violation of maxHeightCM
                if (hasMaxHeightConstraint) {
                    const dimsAtNewBaseHeight = this.calculateTextDimensionsCM(
                        styles.text,
                        styles.font,
                        styleString,
                        newBaseHeight, // Test with the base height derived from width
                        styles.justification
                    );

                    if (dimsAtNewBaseHeight.heightCM > maxHeightCM) {
                        const scaleFactorForHeight = maxHeightCM / dimsAtNewBaseHeight.heightCM;
                        newBaseHeight *= scaleFactorForHeight;
                        newBaseHeight = Math.max(0.01, newBaseHeight); // Ensure positive base height
                    }
                }

                app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(newBaseHeight));
                // Call calculateByBaseHeight to finalize and apply all constraints
                this.calculateByBaseHeight(true, true);

            } catch (err) {
                console.error('Error calculating by width:', err);
                this.calculateByBaseHeight(true, true); // Fallback
            }
        },

        calculateByHeight() {
            const styles = app.CanvasRenderer.getStyles();
            const styleString = app.CanvasRenderer.getStyleString();
            let desiredHeightCM = parseFloat(app.Store.getState('text-height')) || 0;
            let currentBaseHeight = parseFloat(app.Store.getState('text-baseheight')) || 1;
            if (currentBaseHeight <= 0) currentBaseHeight = 1;

            const maxWidthCMString = app.Store.getState('text-maxwidth');
            const maxHeightCMString = app.Store.getState('text-maxheight');
            const maxWidthCM = parseFloat(maxWidthCMString);
            const maxHeightCM = parseFloat(maxHeightCMString);
            const hasMaxWidthConstraint = !isNaN(maxWidthCM) && maxWidthCM > 0;
            const hasMaxHeightConstraint = !isNaN(maxHeightCM) && maxHeightCM > 0;

            // If a max height is set and the desired height exceeds it, cap the desired height.
            if (hasMaxHeightConstraint && desiredHeightCM > maxHeightCM) {
                desiredHeightCM = maxHeightCM;
                // Update the store with properly formatted value
                app.Store.setState('text-height', UnitConverter.formatOutputValue(desiredHeightCM));
            }

            if (styles.text.trim() === '' || desiredHeightCM <= 0) {
                app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(currentBaseHeight));
                this.calculateByBaseHeight(true, true); // This will set width/height to 0 if no text
                if (styles.text.trim() === '') app.Store.setState('text-height', '0.00');
                else if (desiredHeightCM <= 0) app.Store.setState('text-height', '0.00');
                return;
            }

            try {
                // Calculate dimensions with a reference base height to find the scaling factor
                const referenceDims = this.calculateTextDimensionsCM(
                    styles.text,
                    styles.font,
                    styleString,
                    currentBaseHeight, // Use currentBaseHeight as the reference for scaling
                    styles.justification
                );

                if (referenceDims.heightCM <= 0) {
                    console.warn('Calculated reference height is zero or negative. Cannot scale by height.');
                    app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(currentBaseHeight));
                    // Update width based on current baseHeight, height might remain 0 or as is
                    this.calculateByBaseHeight(true, false);
                    return;
                }

                // Calculate precise scale factor without rounding
                const scaleFactorForHeight = desiredHeightCM / referenceDims.heightCM;
                let newBaseHeight = currentBaseHeight * scaleFactorForHeight;
                newBaseHeight = Math.max(0.01, newBaseHeight); // Ensure positive base height

                // Now, check if this newBaseHeight causes a violation of maxWidthCM
                if (hasMaxWidthConstraint) {
                    const dimsAtNewBaseHeight = this.calculateTextDimensionsCM(
                        styles.text,
                        styles.font,
                        styleString,
                        newBaseHeight, // Test with the base height derived from height
                        styles.justification
                    );

                    if (dimsAtNewBaseHeight.widthCM > maxWidthCM) {
                        const scaleFactorForWidth = maxWidthCM / dimsAtNewBaseHeight.widthCM;
                        newBaseHeight *= scaleFactorForWidth;
                        newBaseHeight = Math.max(0.01, newBaseHeight); // Ensure positive base height
                    }
                }

                app.Store.setState('text-baseheight', UnitConverter.formatOutputValue(newBaseHeight));
                // Call calculateByBaseHeight to finalize and apply all constraints
                this.calculateByBaseHeight(true, true);

            } catch (err) {
                console.error('Error calculating by height:', err);
                this.calculateByBaseHeight(true, true); // Fallback
            }
        }
    };

    // Public API
    app.TextMeasurement = {
        calculateByBaseHeight(width = true, height = true) {
            return DimensionCalculator.calculateByBaseHeight.call(DimensionCalculator, width, height);
        },

        calculateByWidth() {
            return DimensionCalculator.calculateByWidth.call(DimensionCalculator);
        },

        calculateByHeight() {
            return DimensionCalculator.calculateByHeight.call(DimensionCalculator);
        },

        testCharacters() {
            const styles = app.CanvasRenderer.getStyles();
            const styleString = app.CanvasRenderer.getStyleString();
            const currentBaseHeight = parseFloat(app.Store.getState('text-baseheight')) || 1;

            console.group('Character Height Test');
            console.log('Testing with base height:', currentBaseHeight, 'cm');

            const testChars = ['H', 'z', 'j', 'g', 'p', 'A', 'X', 'M'];

            const results = [];

            testChars.forEach(char => {
                const dims = DimensionCalculator.calculateTextDimensionsCM(
                    char,
                    styles.font,
                    styleString,
                    currentBaseHeight,
                    'left'
                );

                // Enhanced logging with detailed metrics
                console.log(`Character: ${char}`);
                console.log(`  Height: ${dims.heightCM.toFixed(4)}cm`);
                console.log(`  Width: ${dims.widthCM.toFixed(4)}cm`);
                console.log(`  Cap Height Ratio: ${(dims.pixelMetrics.referenceCapHeightPx / dims.pixelMetrics.heightPx).toFixed(4)}`);

                results.push({
                    char: char,
                    heightCM: dims.heightCM,
                    widthCM: dims.widthCM,
                    ascent: dims.pixelMetrics.firstLineAscentPx,
                    descent: dims.pixelMetrics.lastLineDescentPx,
                    ratio: dims.pixelMetrics.referenceCapHeightPx / dims.pixelMetrics.heightPx
                });
            });

            console.table(results);
            console.groupEnd();

            // Notify debug system if available
            if (typeof window.TextMeasurementDebug !== 'undefined') {
                window.TextMeasurementDebug.onCharacterTest(results);
            }

            return results;
        },

        cleanup() {
            CanvasContext.cleanup();
        },

        getDiagnosticInfo() {
            const styles = app.CanvasRenderer.getStyles();
            const currentBaseHeight = parseFloat(app.Store.getState('text-baseheight')) || 1;

            // Get current dimensions
            const dims = DimensionCalculator.calculateTextDimensionsCM(
                styles.text,
                styles.font,
                app.CanvasRenderer.getStyleString(),
                currentBaseHeight,
                styles.justification
            );

            // Return diagnostics for console inspection
            return {
                dimensions: dims,
                text: styles.text,
                font: styles.font,
                style: app.CanvasRenderer.getStyleString(),
                baseHeight: currentBaseHeight,
                pixelsToCmRatio: CanvasContext.ADOBE_DPI / 2.54,
                context: {
                    canvas: CanvasContext.canvas ? {
                        width: CanvasContext.canvas.width,
                        height: CanvasContext.canvas.height
                    } : null,
                    offscreenCanvas: CanvasContext.offscreenCanvas ? {
                        width: CanvasContext.offscreenCanvas.width,
                        height: CanvasContext.offscreenCanvas.height
                    } : null
                }
            };
        },

        // Expose internal modules for debug access
        _internal: {
            TextMeasurer,
            UnitConverter,
            DimensionCalculator,
            CanvasContext
        }
    };

})(window.app = window.app || {});